// Vendored QR Code encoder.
// Original: Copyright (c) 2009 Kazuhiko Arase, MIT license
//   http://www.d-project.com/  (the qrcode-js library)
// Ported into a single browser-safe TS module: byte mode + ECC M + auto type 1-40.
// We pick this over qrcode.react because the workspace pnpm store is currently
// root-owned, so adding a new dep requires sudo. This file has zero deps.
//
// Strings are encoded as UTF-8 bytes before going into byte mode, so non-ASCII
// payloads also work.

const MODE_8BIT_BYTE = 1 << 2;

const ECC_LEVEL = {
  L: 1,
  M: 0,
  Q: 3,
  H: 2,
} as const;

export type EcLevel = keyof typeof ECC_LEVEL;

const PATTERN_POSITION_TABLE: number[][] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170],
];

// [count, totalCount, dataCount] groups; some rows have two groups concatenated.
// Order per type number: [L, M, Q, H].
const RS_BLOCK_TABLE: number[][] = [
  [1, 26, 19], [1, 26, 16], [1, 26, 13], [1, 26, 9],
  [1, 44, 34], [1, 44, 28], [1, 44, 22], [1, 44, 16],
  [1, 70, 55], [1, 70, 44], [2, 35, 17], [2, 35, 13],
  [1, 100, 80], [2, 50, 32], [2, 50, 24], [4, 25, 9],
  [1, 134, 108], [2, 67, 43], [2, 33, 15, 2, 34, 16], [2, 33, 11, 2, 34, 12],
  [2, 86, 68], [4, 43, 27], [4, 43, 19], [4, 43, 15],
  [2, 98, 78], [4, 49, 31], [2, 32, 14, 4, 33, 15], [4, 39, 13, 1, 40, 14],
  [2, 121, 97], [2, 60, 38, 2, 61, 39], [4, 40, 18, 2, 41, 19], [4, 40, 14, 2, 41, 15],
  [2, 146, 116], [3, 58, 36, 2, 59, 37], [4, 36, 16, 4, 37, 17], [4, 36, 12, 4, 37, 13],
  [2, 86, 68, 2, 87, 69], [4, 69, 43, 1, 70, 44], [6, 43, 19, 2, 44, 20], [6, 43, 15, 2, 44, 16],
  [4, 101, 81], [1, 80, 50, 4, 81, 51], [4, 50, 22, 4, 51, 23], [3, 36, 12, 8, 37, 13],
  [2, 116, 92, 2, 117, 93], [6, 58, 36, 2, 59, 37], [4, 46, 20, 6, 47, 21], [7, 42, 14, 4, 43, 15],
  [4, 133, 107], [8, 59, 37, 1, 60, 38], [8, 44, 20, 4, 45, 21], [12, 33, 11, 4, 34, 12],
  [3, 145, 115, 1, 146, 116], [4, 64, 40, 5, 65, 41], [11, 36, 16, 5, 37, 17], [11, 36, 12, 5, 37, 13],
  [5, 109, 87, 1, 110, 88], [5, 65, 41, 5, 66, 42], [5, 54, 24, 7, 55, 25], [11, 36, 12],
  [5, 122, 98, 1, 123, 99], [7, 73, 45, 3, 74, 46], [15, 43, 19, 2, 44, 20], [3, 45, 15, 13, 46, 16],
  [1, 135, 107, 5, 136, 108], [10, 74, 46, 1, 75, 47], [1, 50, 22, 15, 51, 23], [2, 42, 14, 17, 43, 15],
  [5, 150, 120, 1, 151, 121], [9, 69, 43, 4, 70, 44], [17, 50, 22, 1, 51, 23], [2, 42, 14, 19, 43, 15],
  [3, 141, 113, 4, 142, 114], [3, 70, 44, 11, 71, 45], [17, 47, 21, 4, 48, 22], [9, 39, 13, 16, 40, 14],
  [3, 135, 107, 5, 136, 108], [3, 67, 41, 13, 68, 42], [15, 54, 24, 5, 55, 25], [15, 43, 15, 10, 44, 16],
  [4, 144, 116, 4, 145, 117], [17, 68, 42], [17, 50, 22, 6, 51, 23], [19, 46, 16, 6, 47, 17],
  [2, 139, 111, 7, 140, 112], [17, 74, 46], [7, 54, 24, 16, 55, 25], [34, 37, 13],
  [4, 151, 121, 5, 152, 122], [4, 75, 47, 14, 76, 48], [11, 54, 24, 14, 55, 25], [16, 45, 15, 14, 46, 16],
  [6, 147, 117, 4, 148, 118], [6, 73, 45, 14, 74, 46], [11, 54, 24, 16, 55, 25], [30, 46, 16, 2, 47, 17],
  [8, 132, 106, 4, 133, 107], [8, 75, 47, 13, 76, 48], [7, 54, 24, 22, 55, 25], [22, 45, 15, 13, 46, 16],
  [10, 142, 114, 2, 143, 115], [19, 74, 46, 4, 75, 47], [28, 50, 22, 6, 51, 23], [33, 46, 16, 4, 47, 17],
  [8, 152, 122, 4, 153, 123], [22, 73, 45, 3, 74, 46], [8, 53, 23, 26, 54, 24], [12, 45, 15, 28, 46, 16],
  [3, 147, 117, 10, 148, 118], [3, 73, 45, 23, 74, 46], [4, 54, 24, 31, 55, 25], [11, 45, 15, 31, 46, 16],
  [7, 146, 116, 7, 147, 117], [21, 73, 45, 7, 74, 46], [1, 53, 23, 37, 54, 24], [19, 45, 15, 26, 46, 16],
  [5, 145, 115, 10, 146, 116], [19, 75, 47, 10, 76, 48], [15, 54, 24, 25, 55, 25], [23, 45, 15, 25, 46, 16],
  [13, 145, 115, 3, 146, 116], [2, 74, 46, 29, 75, 47], [42, 54, 24, 1, 55, 25], [23, 45, 15, 28, 46, 16],
  [17, 145, 115], [10, 74, 46, 23, 75, 47], [10, 54, 24, 35, 55, 25], [19, 45, 15, 35, 46, 16],
  [17, 145, 115, 1, 146, 116], [14, 74, 46, 21, 75, 47], [29, 54, 24, 19, 55, 25], [11, 45, 15, 46, 46, 16],
  [13, 145, 115, 6, 146, 116], [14, 74, 46, 23, 75, 47], [44, 54, 24, 7, 55, 25], [59, 46, 16, 1, 47, 17],
  [12, 151, 121, 7, 152, 122], [12, 75, 47, 26, 76, 48], [39, 54, 24, 14, 55, 25], [22, 45, 15, 41, 46, 16],
  [6, 151, 121, 14, 152, 122], [6, 75, 47, 34, 76, 48], [46, 54, 24, 10, 55, 25], [2, 45, 15, 64, 46, 16],
  [17, 152, 122, 4, 153, 123], [29, 74, 46, 14, 75, 47], [49, 54, 24, 10, 55, 25], [24, 45, 15, 46, 46, 16],
  [4, 152, 122, 18, 153, 123], [13, 74, 46, 32, 75, 47], [48, 54, 24, 14, 55, 25], [42, 45, 15, 32, 46, 16],
  [20, 147, 117, 4, 148, 118], [40, 75, 47, 7, 76, 48], [43, 54, 24, 22, 55, 25], [10, 45, 15, 67, 46, 16],
  [19, 148, 118, 6, 149, 119], [18, 75, 47, 31, 76, 48], [34, 54, 24, 34, 55, 25], [20, 45, 15, 61, 46, 16],
];

const G15 =
  (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0);
const G18 =
  (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0);
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1);

// Galois field arithmetic over GF(256). The Uint8Array storage keeps
// values as bytes; the `!` assertions express the algorithm's invariant
// that every index used here is in [0, 256) under
// `noUncheckedIndexedAccess`.
const EXP_TABLE = new Uint8Array(256);
const LOG_TABLE = new Uint8Array(256);
for (let i = 0; i < 8; i++) EXP_TABLE[i] = 1 << i;
for (let i = 8; i < 256; i++) {
  EXP_TABLE[i] =
    EXP_TABLE[i - 4]! ^ EXP_TABLE[i - 5]! ^ EXP_TABLE[i - 6]! ^ EXP_TABLE[i - 8]!;
}
for (let i = 0; i < 255; i++) LOG_TABLE[EXP_TABLE[i]!] = i;

function gexp(n: number): number {
  while (n < 0) n += 255;
  while (n >= 256) n -= 255;
  return EXP_TABLE[n]!;
}
function glog(n: number): number {
  if (n < 1) throw new Error(`glog(${n})`);
  return LOG_TABLE[n]!;
}

class Polynomial {
  num: number[];
  constructor(num: number[], shift = 0) {
    let offset = 0;
    while (offset < num.length && num[offset] === 0) offset++;
    this.num = new Array(num.length - offset + shift).fill(0);
    for (let i = 0; i < num.length - offset; i++) this.num[i] = num[i + offset]!;
  }
  // All Polynomial accesses below are bounded by getLength(); the `!`
  // expresses that invariant under `noUncheckedIndexedAccess`.
  get(i: number): number {
    return this.num[i]!;
  }
  getLength() {
    return this.num.length;
  }
  multiply(e: Polynomial): Polynomial {
    const num = new Array<number>(this.getLength() + e.getLength() - 1).fill(0);
    for (let i = 0; i < this.getLength(); i++) {
      for (let j = 0; j < e.getLength(); j++) {
        num[i + j] = num[i + j]! ^ gexp(glog(this.get(i)) + glog(e.get(j)));
      }
    }
    return new Polynomial(num, 0);
  }
  mod(e: Polynomial): Polynomial {
    if (this.getLength() - e.getLength() < 0) return this;
    const ratio = glog(this.get(0)) - glog(e.get(0));
    const num = new Array<number>(this.getLength());
    for (let i = 0; i < this.getLength(); i++) num[i] = this.get(i);
    for (let i = 0; i < e.getLength(); i++) num[i] = num[i]! ^ gexp(glog(e.get(i)) + ratio);
    return new Polynomial(num, 0).mod(e);
  }
}

function getErrorCorrectPolynomial(ecLen: number): Polynomial {
  let a = new Polynomial([1], 0);
  for (let i = 0; i < ecLen; i++) {
    a = a.multiply(new Polynomial([1, gexp(i)], 0));
  }
  return a;
}

function getBCHDigit(data: number): number {
  let digit = 0;
  while (data !== 0) {
    digit++;
    data >>>= 1;
  }
  return digit;
}

function getBCHTypeInfo(data: number): number {
  let d = data << 10;
  while (getBCHDigit(d) - getBCHDigit(G15) >= 0) {
    d ^= G15 << (getBCHDigit(d) - getBCHDigit(G15));
  }
  return ((data << 10) | d) ^ G15_MASK;
}

function getBCHTypeNumber(data: number): number {
  let d = data << 12;
  while (getBCHDigit(d) - getBCHDigit(G18) >= 0) {
    d ^= G18 << (getBCHDigit(d) - getBCHDigit(G18));
  }
  return (data << 12) | d;
}

function getMask(maskPattern: number, i: number, j: number): boolean {
  switch (maskPattern) {
    case 0: return (i + j) % 2 === 0;
    case 1: return i % 2 === 0;
    case 2: return j % 3 === 0;
    case 3: return (i + j) % 3 === 0;
    case 4: return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0;
    case 5: return ((i * j) % 2) + ((i * j) % 3) === 0;
    case 6: return (((i * j) % 2) + ((i * j) % 3)) % 2 === 0;
    case 7: return (((i * j) % 3) + ((i + j) % 2)) % 2 === 0;
    default: throw new Error(`bad maskPattern: ${maskPattern}`);
  }
}

function getLengthInBits(type: number): number {
  // byte mode only
  if (type >= 1 && type < 10) return 8;
  if (type < 27) return 16;
  if (type < 41) return 16;
  throw new Error(`type: ${type}`);
}

function getRsBlockTable(typeNumber: number, ecLevel: EcLevel): number[] | undefined {
  const ec = ECC_LEVEL[ecLevel];
  // Storage layout: row index = (typeNumber - 1) * 4 + offset; offset by ECC L=0,M=1,Q=2,H=3.
  const offset = ec === 1 ? 0 : ec === 0 ? 1 : ec === 3 ? 2 : 3;
  return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + offset];
}

function getRSBlocks(
  typeNumber: number,
  ecLevel: EcLevel,
): Array<{ totalCount: number; dataCount: number }> {
  const rsBlock = getRsBlockTable(typeNumber, ecLevel);
  if (!rsBlock) throw new Error(`bad rs block @ ${typeNumber}/${ecLevel}`);
  const list: Array<{ totalCount: number; dataCount: number }> = [];
  for (let i = 0; i < rsBlock.length / 3; i++) {
    const count = rsBlock[i * 3]!;
    const totalCount = rsBlock[i * 3 + 1]!;
    const dataCount = rsBlock[i * 3 + 2]!;
    for (let j = 0; j < count; j++) list.push({ totalCount, dataCount });
  }
  return list;
}

class BitBuffer {
  buffer: number[] = [];
  length = 0;
  put(num: number, len: number) {
    for (let i = 0; i < len; i++) {
      this.putBit(((num >>> (len - i - 1)) & 1) === 1);
    }
  }
  putBit(bit: boolean) {
    const bufIndex = Math.floor(this.length / 8);
    if (this.buffer.length <= bufIndex) this.buffer.push(0);
    if (bit) this.buffer[bufIndex] = this.buffer[bufIndex]! | (0x80 >>> this.length % 8);
    this.length++;
  }
  getLengthInBits() {
    return this.length;
  }
}

function utf8Bytes(input: string): number[] {
  if (typeof TextEncoder !== 'undefined') {
    return Array.from(new TextEncoder().encode(input));
  }
  // Fallback (older runtimes): manual UTF-8 encoding.
  const out: number[] = [];
  for (let i = 0; i < input.length; i++) {
    let c = input.charCodeAt(i);
    if (c < 0x80) {
      out.push(c);
    } else if (c < 0x800) {
      out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if ((c & 0xfc00) === 0xd800 && i + 1 < input.length) {
      const c2 = input.charCodeAt(++i);
      const code = 0x10000 + (((c & 0x3ff) << 10) | (c2 & 0x3ff));
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    } else {
      out.push(
        0xe0 | (c >> 12),
        0x80 | ((c >> 6) & 0x3f),
        0x80 | (c & 0x3f),
      );
    }
  }
  return out;
}

const PAD0 = 0xec;
const PAD1 = 0x11;

class QrEncoder {
  typeNumber: number;
  ecLevel: EcLevel;
  modules: (boolean | null)[][] = [];
  moduleCount = 0;
  data: number[];

  constructor(text: string, ecLevel: EcLevel = 'M') {
    this.ecLevel = ecLevel;
    this.data = utf8Bytes(text);
    this.typeNumber = this.pickTypeNumber();
  }

  private pickTypeNumber(): number {
    for (let t = 1; t < 41; t++) {
      const rsBlocks = getRSBlocks(t, this.ecLevel);
      let totalDataCount = 0;
      for (const b of rsBlocks) totalDataCount += b.dataCount;
      // 4 mode bits + length bits + 8 * data length
      const lenBits = getLengthInBits(t);
      const need = 4 + lenBits + 8 * this.data.length;
      if (need <= totalDataCount * 8) return t;
    }
    throw new Error('input too long for QR (max version 40)');
  }

  encode(): { size: number; modules: boolean[][] } {
    let bestPattern = 0;
    let bestPoints = Number.POSITIVE_INFINITY;
    for (let p = 0; p < 8; p++) {
      this.makeImpl(true, p);
      const pts = this.getLostPoint();
      if (pts < bestPoints) {
        bestPoints = pts;
        bestPattern = p;
      }
    }
    this.makeImpl(false, bestPattern);
    return {
      size: this.moduleCount,
      modules: this.modules.map((row) => row.map((c) => c === true)),
    };
  }

  private makeImpl(test: boolean, maskPattern: number) {
    this.moduleCount = this.typeNumber * 4 + 17;
    this.modules = new Array(this.moduleCount);
    for (let r = 0; r < this.moduleCount; r++) {
      this.modules[r] = new Array(this.moduleCount).fill(null);
    }
    this.setupPositionProbePattern(0, 0);
    this.setupPositionProbePattern(this.moduleCount - 7, 0);
    this.setupPositionProbePattern(0, this.moduleCount - 7);
    this.setupPositionAdjustPattern();
    this.setupTimingPattern();
    this.setupTypeInfo(test, maskPattern);
    if (this.typeNumber >= 7) this.setupTypeNumber(test);
    const cache = this.createData();
    this.mapData(cache, maskPattern);
  }

  // Helpers centralise the non-null assertion on `this.modules[r]`.
  // The algorithm guarantees `r` and `c` are both in range whenever a
  // helper is called, so under `noUncheckedIndexedAccess` we can
  // safely assert the row exists.
  private mSet(r: number, c: number, v: boolean | null) {
    this.modules[r]![c] = v;
  }
  private mGet(r: number, c: number): boolean | null {
    return this.modules[r]![c] ?? null;
  }

  private setupPositionProbePattern(row: number, col: number) {
    for (let r = -1; r <= 7; r++) {
      if (row + r <= -1 || this.moduleCount <= row + r) continue;
      for (let c = -1; c <= 7; c++) {
        if (col + c <= -1 || this.moduleCount <= col + c) continue;
        const onEdge =
          (0 <= r && r <= 6 && (c === 0 || c === 6)) ||
          (0 <= c && c <= 6 && (r === 0 || r === 6)) ||
          (2 <= r && r <= 4 && 2 <= c && c <= 4);
        this.mSet(row + r, col + c, onEdge);
      }
    }
  }

  private setupTimingPattern() {
    for (let r = 8; r < this.moduleCount - 8; r++) {
      if (this.mGet(r, 6) === null) this.mSet(r, 6, r % 2 === 0);
    }
    for (let c = 8; c < this.moduleCount - 8; c++) {
      if (this.mGet(6, c) === null) this.mSet(6, c, c % 2 === 0);
    }
  }

  private setupPositionAdjustPattern() {
    const pos = PATTERN_POSITION_TABLE[this.typeNumber - 1] ?? [];
    for (let i = 0; i < pos.length; i++) {
      for (let j = 0; j < pos.length; j++) {
        const row = pos[i]!;
        const col = pos[j]!;
        if (this.mGet(row, col) !== null) continue;
        for (let r = -2; r <= 2; r++) {
          for (let c = -2; c <= 2; c++) {
            const dark =
              Math.abs(r) === 2 ||
              Math.abs(c) === 2 ||
              (r === 0 && c === 0);
            this.mSet(row + r, col + c, dark);
          }
        }
      }
    }
  }

  private setupTypeNumber(test: boolean) {
    const bits = getBCHTypeNumber(this.typeNumber);
    for (let i = 0; i < 18; i++) {
      const mod = !test && ((bits >> i) & 1) === 1;
      this.mSet(Math.floor(i / 3), (i % 3) + this.moduleCount - 8 - 3, mod);
    }
    for (let i = 0; i < 18; i++) {
      const mod = !test && ((bits >> i) & 1) === 1;
      this.mSet((i % 3) + this.moduleCount - 8 - 3, Math.floor(i / 3), mod);
    }
  }

  private setupTypeInfo(test: boolean, maskPattern: number) {
    const data = (ECC_LEVEL[this.ecLevel] << 3) | maskPattern;
    const bits = getBCHTypeInfo(data);
    for (let v = 0; v < 15; v++) {
      const mod = !test && ((bits >> v) & 1) === 1;
      if (v < 6) this.mSet(v, 8, mod);
      else if (v < 8) this.mSet(v + 1, 8, mod);
      else this.mSet(this.moduleCount - 15 + v, 8, mod);
    }
    for (let h = 0; h < 15; h++) {
      const mod = !test && ((bits >> h) & 1) === 1;
      if (h < 8) this.mSet(8, this.moduleCount - h - 1, mod);
      else if (h < 9) this.mSet(8, 15 - h - 1 + 1, mod);
      else this.mSet(8, 15 - h - 1, mod);
    }
    this.mSet(this.moduleCount - 8, 8, !test);
  }

  private mapData(data: number[], maskPattern: number) {
    let inc = -1;
    let row = this.moduleCount - 1;
    let bitIndex = 7;
    let byteIndex = 0;
    for (let col = this.moduleCount - 1; col > 0; col -= 2) {
      if (col === 6) col--;
      while (true) {
        for (let c = 0; c < 2; c++) {
          if (this.mGet(row, col - c) === null) {
            let dark = false;
            if (byteIndex < data.length) {
              dark = ((data[byteIndex]! >>> bitIndex) & 1) === 1;
            }
            const mask = getMask(maskPattern, row, col - c);
            if (mask) dark = !dark;
            this.mSet(row, col - c, dark);
            bitIndex--;
            if (bitIndex === -1) {
              byteIndex++;
              bitIndex = 7;
            }
          }
        }
        row += inc;
        if (row < 0 || this.moduleCount <= row) {
          row -= inc;
          inc = -inc;
          break;
        }
      }
    }
  }

  private createData(): number[] {
    const rsBlocks = getRSBlocks(this.typeNumber, this.ecLevel);
    const buffer = new BitBuffer();
    buffer.put(MODE_8BIT_BYTE, 4);
    buffer.put(this.data.length, getLengthInBits(this.typeNumber));
    for (const b of this.data) buffer.put(b, 8);

    let totalDataCount = 0;
    for (const b of rsBlocks) totalDataCount += b.dataCount;
    if (buffer.getLengthInBits() > totalDataCount * 8) {
      throw new Error('code length overflow');
    }
    if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) buffer.put(0, 4);
    while (buffer.getLengthInBits() % 8 !== 0) buffer.putBit(false);
    while (true) {
      if (buffer.getLengthInBits() >= totalDataCount * 8) break;
      buffer.put(PAD0, 8);
      if (buffer.getLengthInBits() >= totalDataCount * 8) break;
      buffer.put(PAD1, 8);
    }
    return this.createBytes(buffer, rsBlocks);
  }

  private createBytes(
    buffer: BitBuffer,
    rsBlocks: Array<{ totalCount: number; dataCount: number }>,
  ): number[] {
    let offset = 0;
    let maxDc = 0;
    let maxEc = 0;
    const dcdata: number[][] = new Array(rsBlocks.length);
    const ecdata: number[][] = new Array(rsBlocks.length);
    for (let r = 0; r < rsBlocks.length; r++) {
      const block = rsBlocks[r]!;
      const dcCount = block.dataCount;
      const ecCount = block.totalCount - dcCount;
      maxDc = Math.max(maxDc, dcCount);
      maxEc = Math.max(maxEc, ecCount);
      const dcrow: number[] = new Array(dcCount);
      for (let i = 0; i < dcCount; i++) dcrow[i] = 0xff & (buffer.buffer[i + offset] ?? 0);
      dcdata[r] = dcrow;
      offset += dcCount;
      const rsPoly = getErrorCorrectPolynomial(ecCount);
      const rawPoly = new Polynomial(dcrow, rsPoly.getLength() - 1);
      const modPoly = rawPoly.mod(rsPoly);
      const ecrow: number[] = new Array(rsPoly.getLength() - 1);
      for (let i = 0; i < ecrow.length; i++) {
        const modIndex = i + modPoly.getLength() - ecrow.length;
        ecrow[i] = modIndex >= 0 ? modPoly.get(modIndex) : 0;
      }
      ecdata[r] = ecrow;
    }
    let totalCodeCount = 0;
    for (const b of rsBlocks) totalCodeCount += b.totalCount;
    const data = new Array<number>(totalCodeCount);
    let index = 0;
    for (let i = 0; i < maxDc; i++) {
      for (let r = 0; r < rsBlocks.length; r++) {
        const dcrow = dcdata[r]!;
        if (i < dcrow.length) data[index++] = dcrow[i]!;
      }
    }
    for (let i = 0; i < maxEc; i++) {
      for (let r = 0; r < rsBlocks.length; r++) {
        const ecrow = ecdata[r]!;
        if (i < ecrow.length) data[index++] = ecrow[i]!;
      }
    }
    return data;
  }

  private getLostPoint(): number {
    const n = this.moduleCount;
    let lost = 0;
    // LEVEL 1: runs of same colour in 3x3 neighbourhood
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        let same = 0;
        const dark = this.isDark(row, col);
        for (let r = -1; r <= 1; r++) {
          if (row + r < 0 || n <= row + r) continue;
          for (let c = -1; c <= 1; c++) {
            if (col + c < 0 || n <= col + c) continue;
            if (r === 0 && c === 0) continue;
            if (dark === this.isDark(row + r, col + c)) same++;
          }
        }
        if (same > 5) lost += 3 + same - 5;
      }
    }
    // LEVEL 2: 2x2 monochrome blocks
    for (let row = 0; row < n - 1; row++) {
      for (let col = 0; col < n - 1; col++) {
        let count = 0;
        if (this.isDark(row, col)) count++;
        if (this.isDark(row + 1, col)) count++;
        if (this.isDark(row, col + 1)) count++;
        if (this.isDark(row + 1, col + 1)) count++;
        if (count === 0 || count === 4) lost += 3;
      }
    }
    // LEVEL 3: 1:1:3:1:1 pattern (finder-pattern look-alike)
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n - 6; col++) {
        if (
          this.isDark(row, col) &&
          !this.isDark(row, col + 1) &&
          this.isDark(row, col + 2) &&
          this.isDark(row, col + 3) &&
          this.isDark(row, col + 4) &&
          !this.isDark(row, col + 5) &&
          this.isDark(row, col + 6)
        ) lost += 40;
      }
    }
    for (let col = 0; col < n; col++) {
      for (let row = 0; row < n - 6; row++) {
        if (
          this.isDark(row, col) &&
          !this.isDark(row + 1, col) &&
          this.isDark(row + 2, col) &&
          this.isDark(row + 3, col) &&
          this.isDark(row + 4, col) &&
          !this.isDark(row + 5, col) &&
          this.isDark(row + 6, col)
        ) lost += 40;
      }
    }
    // LEVEL 4: dark/light balance
    let darkCount = 0;
    for (let col = 0; col < n; col++) {
      for (let row = 0; row < n; row++) {
        if (this.isDark(row, col)) darkCount++;
      }
    }
    const ratio = Math.abs((100 * darkCount) / n / n - 50) / 5;
    lost += ratio * 10;
    return lost;
  }

  private isDark(row: number, col: number): boolean {
    return this.mGet(row, col) === true;
  }
}

export function encodeQrMatrix(
  text: string,
  ecLevel: EcLevel = 'M',
): { size: number; modules: boolean[][] } {
  return new QrEncoder(text, ecLevel).encode();
}
